import React, { useState } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import { useDispatch, useSelector } from 'react-redux'
import { withRouter } from 'react-router-dom'
import { Button } from '../root/components/common'
import CloseIcon from '@material-ui/icons/Close'
import * as actions from './actions'
import { createSelector } from 'reselect'
import { getAvailableDevicesToFloorPlans } from './selectors'
import { debounce } from 'lodash'
import { DevicesTree } from './components/devices-tree/devices-tree'
import { useCallback } from 'react'
import { useTreeUtils } from '../root/components/common/locations-tree/tree-utils'
import { shouldWarnAboutRetainedLocationSettingsOverrideForMoves } from './utils/device-location-settings-override'
import { LocationSettingsOverrideMoveConfirmationModal } from './components/device-location-settings-override-move-confirmation'
import { enqueueSnackbar } from '../root/actions'

const getAvailableDevicesToFloorPlansSelector = createSelector(
    state => state.locations.availableDevicesToFloorPlans,
    state => state.main.locale,
    (availableDevices, locale) =>
        getAvailableDevicesToFloorPlans(availableDevices, locale)
)

const useStyles = makeStyles(theme => ({
    wrapper: {
        height: '100%'
    },
    topButtonsWrapper: {
        display: 'flex',
        alignItems: 'end',
        justifyContent: 'flex-end',
        marginBottom: 8
    },
    closeIconWrapper: {
        display: 'flex',
        alignItems: 'center'
    },
    closeIcon: {
        width: 16,
        height: 16,
        marginLeft: 4
    }
}))

function DevicesToFloorPlan() {
    const classes = useStyles()
    const dispatch = useDispatch()

    const { locale } = useSelector(state => state.main)
    const [search, setSearch] = useState('')
    const [pendingDevice, setPendingDevice] = useState(null)

    const { isLocationsSidebarLoading, floorPlan, location } = useSelector(
        state => state.locations
    )

    const { availableDevicesTree } = useSelector(
        getAvailableDevicesToFloorPlansSelector
    )

    const treeUtils = useTreeUtils(availableDevicesTree)

    const onCancelClick = () => {
        dispatch(actions.setIsAddDevicesToFloorPlanMode(false))
    }

    const moveDeviceToFloorPlan = device => {
        const isNetworkDevice = device.hasOwnProperty('router_type')
        const moveRequestMethodName = isNetworkDevice
            ? 'moveNetworkDevicesRequest'
            : 'moveDevicesRequest'
        dispatch(
            actions[moveRequestMethodName](
                [
                    {
                        id: Number(device.id),
                        location_id: Number(location.id),
                        customer_id: Number(floorPlan.customer_id)
                    }
                ],
                true
            )
        )
        treeUtils.deleteNode(device.id)
    }

    const onAddDeviceToFloorPlan = async device => {
        const isNetworkDevice = device.hasOwnProperty('router_type')

        if (!isNetworkDevice) {
            try {
                const shouldWarn =
                    await shouldWarnAboutRetainedLocationSettingsOverrideForMoves(
                        [{ devices: [device], locationId: location.id }]
                    )
                if (shouldWarn) {
                    setPendingDevice(device)
                    return
                }
            } catch {
                dispatch(
                    enqueueSnackbar({
                        message: locale.sagasMsgs.commonLoadingError,
                        options: {
                            variant: 'error'
                        }
                    })
                )
                return
            }
        }

        moveDeviceToFloorPlan(device)
    }

    const handleSearch = useCallback(
        debounce(v => {
            setSearch(v)
            dispatch(
                actions.availableDevicesToFloorPlansRequest(
                    location.id,
                    location.customer_id,
                    {
                        search: v
                    }
                )
            )
        }, 300),
        []
    )

    return (
        <div className={classes.wrapper}>
            <div className={classes.topButtonsWrapper}>
                <Button onClick={onCancelClick}>
                    <div>{locale.common.done}</div>
                    <div className={classes.closeIconWrapper}>
                        <CloseIcon className={classes.closeIcon} />
                    </div>
                </Button>
            </div>

            <DevicesTree
                isLoading={isLocationsSidebarLoading}
                search={search}
                allExpanded={Boolean(search)}
                onSearch={handleSearch}
                treeUtils={treeUtils}
                onIconClick={onAddDeviceToFloorPlan}
            />
            <LocationSettingsOverrideMoveConfirmationModal
                open={Boolean(pendingDevice)}
                onConfirm={() => {
                    moveDeviceToFloorPlan(pendingDevice)
                    setPendingDevice(null)
                }}
                onCancel={() => setPendingDevice(null)}
                locale={locale}
            />
        </div>
    )
}

export default withRouter(DevicesToFloorPlan)
